import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { TestExecutionSyncFilters, TestExecutionSyncOrder } from '@/gql/graphql'
import { prettyDateTime } from '@/helpers/text_formatters'
import { StateBadge } from './state_badge'
import { ResultBadge } from '../test_executions/result_badge'

const TestExecutionSyncsQuery = graphql(`
  query TestExecutionSyncs($filters: TestExecutionSyncFilters, $order: TestExecutionSyncOrder, $after: String) {
    testExecutionSyncs(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        testExecution { id pass equipment { id barcode name } }
        state
        errorReason
        updatedAt
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: TestExecutionSyncFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(TestExecutionSyncsQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.testExecutionSyncs?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<TestExecutionSyncFilters, TestExecutionSyncOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.testExecutionSyncs.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.testExecutionSyncs.nodes = dedupe([...previousData.testExecutionSyncs.nodes, ...fetchMoreResult.testExecutionSyncs.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        filters={ [
          {
            index: 0,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'barcodeCont',
          },
          {
            index: 1,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'nameCont',
          },
          {
            index: 2,
            type: 'select',
            filterKey: 'passEq',
            options: [
              { value: true, display:  <ResultBadge pass={true}/> } ,
              { value: false, display: <ResultBadge pass={false} /> },
            ]
          },
          {
            index: 3,
            type: 'select',
            filterKey: 'stateEq',
            options: [
              { value: 'pending', display: <StateBadge state='pending'/>},
              { value: 'failed', display: <StateBadge state='failed'/> },
              { value: 'processing', display: <StateBadge state='processing'/> },
              { value: 'succeeded', display: <StateBadge state='succeeded'/> },
            ]
          },
        ] }
        headings={ [
          { name: 'Barcode' },
          { name: 'Equipment name' },
          { name: 'Test result' },
          { name: 'Sync state' },
          { name: 'Sync error' },
          { name: 'Last update'}
        ] }
        rows={ data?.testExecutionSyncs?.nodes.map(testExecutionSync => ({
          id: testExecutionSync.id,
          columns: [
            testExecutionSync.testExecution.equipment?.barcode || 'Unknown',
            testExecutionSync.testExecution.equipment?.name || 'Unknown',
            testExecutionSync.testExecution.pass && <ResultBadge pass={testExecutionSync.testExecution.pass}/>,
            <StateBadge state={testExecutionSync.state}/>,
            testExecutionSync.errorReason,
            prettyDateTime(new Date(testExecutionSync.updatedAt as string)),
          ],
        })) }
      /> }
    </>
  )
}
